import { Injectable, InjectionToken, computed, effect, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';

export type AppLocale = 'en' | 'ar';
export type Direction = 'ltr' | 'rtl';

const STORAGE_KEY = 'nabd.locale';

/**
 * The language every tab opens in: Arabic, the language the hospital works in.
 *
 * Not the browser's language. Most workstations here run an English browser
 * whatever their users read, so following the browser opened Nabd in English
 * for nearly everyone. English stays one click away on every screen, sign-in
 * included, and a choice made in a tab holds in that tab.
 */
export const HOSPITAL_LOCALE: AppLocale = 'ar';

/** Where {@link LocaleService} reads the opening language; tests replace it. */
export const DEFAULT_LOCALE = new InjectionToken<AppLocale>('nabd.default-locale', {
  providedIn: 'root',
  factory: () => HOSPITAL_LOCALE,
});

export interface LocaleDefinition {
  readonly code: AppLocale;
  readonly direction: Direction;
  /** Endonym, shown in the switcher in its own script. */
  readonly label: string;
  /**
   * BCP-47 tag used for `Intl` formatting of *narrative* values — relative
   * times, month names, long dates.
   */
  readonly intlTag: string;
}

export const LOCALES: readonly LocaleDefinition[] = [
  { code: 'en', direction: 'ltr', label: 'English', intlTag: 'en-GB' },
  { code: 'ar', direction: 'rtl', label: 'العربية', intlTag: 'ar-EG' },
] as const;

/**
 * Application locale and text direction.
 *
 * Runtime switching is a requirement, not a convenience. A NICU workstation is
 * shared: an Arabic-first nurse and an English-first registrar use the same
 * screen minutes apart, mid-shift. Angular's compile-time i18n would need a
 * separate bundle per locale and a full page reload to switch, which loses
 * unsaved form state and drops the telemetry socket. Transloco resolves
 * translations at runtime, so switching is instant and stateless.
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly transloco = inject(TranslocoService);
  private readonly document = inject(DOCUMENT);
  // Before `current`: its initial value reads this.
  private readonly opening = inject(DEFAULT_LOCALE);

  private readonly current = signal<AppLocale>(this.readInitial());

  readonly locale = this.current.asReadonly();

  readonly definition = computed<LocaleDefinition>(
    () => LOCALES.find((l) => l.code === this.current()) ?? LOCALES[0],
  );

  readonly direction = computed<Direction>(() => this.definition().direction);

  readonly isRtl = computed(() => this.direction() === 'rtl');

  /** BCP-47 tag for narrative Intl formatting. Not for clinical values. */
  readonly intlTag = computed(() => this.definition().intlTag);

  constructor() {
    effect(() => {
      const def = this.definition();
      const root = this.document.documentElement;

      root.setAttribute('lang', def.code);
      root.setAttribute('dir', def.direction);
      // Lets component styles branch on locale without injecting the service.
      root.setAttribute('data-locale', def.code);

      this.transloco.setActiveLang(def.code);

      try {
        // sessionStorage, deliberately. The quality gate forbids persistent
        // browser storage on this clinical application; a per-tab locale also
        // means a shared NICU workstation reverts to Arabic for the next
        // clinician instead of inheriting the previous user's choice.
        this.document.defaultView?.sessionStorage?.setItem(STORAGE_KEY, def.code);
      } catch {
        // Private browsing or a locked-down kiosk profile. The locale still
        // applies for this session; only persistence is lost.
      }
    });
  }

  set(locale: AppLocale): void {
    this.current.set(locale);
  }

  toggle(): void {
    this.current.update((c) => (c === 'en' ? 'ar' : 'en'));
  }

  private readInitial(): AppLocale {
    try {
      const stored = this.document.defaultView?.sessionStorage?.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'ar') {
        return stored;
      }
    } catch {
      // ignore
    }

    return this.opening;
  }
}
